import {useLocation} from 'react-router';
import React from 'react';
import ModalContainer from 'react-modal-promise';

import {ReactErrorBoundary} from 'common/components/react-error-boundary';
import {useHasGlobalSidenav} from 'iron/routes/utils';
import {OnboardingQuestionnaire} from 'common/onboarding/questionnaire';
import {ProductTour} from 'common/components/product-tour';
import {GlobalSidenav} from 'iron/routes/global-sidenav';
import {GlobalSidenavLayoutProps} from 'iron/routes/global-sidenav/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useTours} from 'common/contexts/react/tours-context';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {isCompanyProfileEnabled} from 'common/report/util/feature-sampling';
import {useImmersiveOnboarding, useNavigationEffects} from 'iron/routes/global-sidenav/layout/hooks';
import {ContentSidenavProvider} from 'common/widgets/content-sidenav/context';
import {TourName} from 'common/util/tour-constants';

import styles from './index.module.styl';

function LayoutInner(props: GlobalSidenavLayoutProps) {
  const currentProject = useCurrentProject().get().value;
  const hasGlobalSidenav = useHasGlobalSidenav();
  const tours = useTours();
  const location = useLocation();

  useNavigationEffects();
  useImmersiveOnboarding();

  const pageContentWithErrorBoundary = (
    <ReactErrorBoundary className={styles.pageContentErrorBoundary}>{props.children}</ReactErrorBoundary>
  );

  // NOTE: THIS VARIABLE is sync and never updates for special cases for download mode / embed mode
  // make sure this is never a async variable. Other wise the global loader position is going to shift and tests start to flake.
  if (!hasGlobalSidenav) {
    return (
      <div className={styles.pageContentContainer} elref='pageContentContainer'>
        {pageContentWithErrorBoundary}
      </div>
    );
  }

  return (
    <>
      <div className={styles.globalSidenavLayout}>
        <div className={styles.globalSidenavContainer}>
          <ReactErrorBoundary className={styles.globalSidenavErrorBoundary}>
            <GlobalSidenav {...props} location={location} />
          </ReactErrorBoundary>
        </div>
        <div className={styles.pageContentContainer} elref='pageContentContainer'>
          {pageContentWithErrorBoundary}
          {tours.isShowingGlobalSidenavTour() && <div className={styles.tourBackgroundBlur} />}
        </div>
      </div>
      <div id='modals'>
        {/* Ensure that modals are appended to the end of the body for the rare cases where multiple modals are opened
        and yes, the library misspelled instances */}
        <ModalContainer isAppendIntances />
        {currentProject && (
          <>
            <ProductTour />
            <OnboardingQuestionnaire />
          </>
        )}
      </div>
      <div id='tooltips' />
    </>
  );
}

// This layout assumes it has `/project/:project_id` in the url
// NOTE: THIS IS VERY IMPORTANT!!
// DO NOT DO ASYNC LOADING here to mutate the DOM structure.
// our testcases relies on a stable DOM structure to assume the lazy loading spinner is gone before start to do assertions
export function GlobalSidenavLayout(props: GlobalSidenavLayoutProps) {
  const currentProject = useCurrentProject().get().value;
  const tours = useTours();
  React.useEffect(() => {
    dataGroupStore.subscribe(() => {
      if (isCompanyProfileEnabled(currentProject) && Object.keys(dataGroupStore.value).length > 0) {
        tours.openTour(TourName.B2B_ANALYTICS);
      }
    });
  }, [currentProject, tours]);

  return (
    <ReactErrorBoundary>
      <ContentSidenavProvider>
        <LayoutInner {...props}>{props.children}</LayoutInner>
      </ContentSidenavProvider>
    </ReactErrorBoundary>
  );
}

// This is specificity used for admin-org settings app. project_id is not on the url so we don't have currentProject context
export function EmptySidenavLayout(props: GlobalSidenavLayoutProps) {
  return (
    <ReactErrorBoundary>
      <LayoutInner {...props}>{props.children}</LayoutInner>
    </ReactErrorBoundary>
  );
}
